import { createContext, useContext, useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import { getItem, setItem } from "./SessionStorage.js";
import { noteAuth } from "./refreshSession.js";
import { completeAuthData } from "./sessionIdentity.js";

export const AuthContext = createContext({});

// Session stored in sessionStorage ("authToken"): kept only with an unexpired token that carries a role.
const readStoredSession = () => {
  try {
    const authData = getItem("authToken");
    const decoded = jwtDecode(authData?.accessToken || undefined);

    if ((decoded?.exp || 0) * 1000 > Date.now() && decoded?.UserInfo?.role) {
      // dopo un'impersonazione/F5 in sessionStorage c'e' solo l'accessToken:
      // user_id, role, type... si derivano dal token (helper unico)
      return { authData: completeAuthData(authData), decoded };
    }
    return {};
  } catch (e) {
    console.error(e);
    return {};
  }
};

// onAuthChange(decoded, { authData, restored }): called when a token is restored at startup (restored)
// or set with setAuth; the app syncs its own state there (user, settings...). Not called on logout.
export const AuthProvider = ({ onAuthChange, children }) => {
  const [auth, setToken] = useState(() => readStoredSession()?.authData || {});

  useEffect(() => {
    const { authData, decoded } = readStoredSession();
    if (authData) {
      setToken(authData);
      noteAuth(authData);
      onAuthChange?.(decoded, { authData, restored: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- startup only

  const setAuth = (value) => {
    setToken(value);
    // token corrente (sincrono) e, se valido, riarma la chiusura di sessione
    noteAuth(value);

    if (!value?.accessToken) {
      // logout / auth vuota: nessun token da decodificare
      setItem("authToken", value);
      return;
    }

    const decoded = jwtDecode(value.accessToken);
    onAuthChange?.(decoded, { authData: value, restored: false });
    setItem("authToken", value);
  };

  return <AuthContext.Provider value={{ auth, setAuth }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
